import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Alert } from 'react-bootstrap';
import {
  required,
  facilityCodeValidation,
  venueCodeValidation,
} from '../../Helpers/validators';
import PopoverTrigger from '../Popover';
import {
  normalizeFacilityCode,
  normalizeVenueCode,
} from '../../Helpers/normalizers';
import VenueCodeField from './VenueCodeField';
import SpecialTaxFields from './SpecialTaxFields';
import venuePropType from '../../Constants/venueTypes';

const SearchByVenueCode = ({
  venueCodeChecked,
  validVenueCode,
  searchVenueCode,
  facilityCode,
  venueCode,
  venueName,
  address,
  isBlocked,
  genericServerError,
  hasRegionalTax,
  taxRegions,
  isKentuckyEntity,
  setFormKentuckyCity,
}) => {
  const formError = venueCodeChecked && !validVenueCode;
  const venueCodeCheck = useRef({
    venueCode,
    facilityCode,
    venueCodeChecked,
    searchVenueCode,
  });

  useEffect(() => {
    const { venueCode, facilityCode, venueCodeChecked, searchVenueCode } =
      venueCodeCheck.current;
    if (venueCode && facilityCode && !venueCodeChecked) {
      searchVenueCode(facilityCode, venueCode);
    }
  }, [venueCodeCheck]);

  return (
    <>
      <div className="d-flex align-items-center">
        <span
          className={
            formError ? 'label form-error__label my-auto' : 'label my-auto'
          }
        >
          Venue code
        </span>
        <PopoverTrigger content="Some participating venues provide a short code to identify their location for the event holders who are renting their facility. The code can usually be found on their website, rental brochures or through the location manager who facilitates the rental contracts." />
      </div>
      {formError && (
        <p className="form-error">
          The venue location for that code could not be found.
        </p>
      )}

      <div className="d-flex align-items-center form-group">
        <Field
          component={VenueCodeField}
          className={`form-control form-control-lg form-control--mw-100 text-center ${
            formError && 'venue-code-form-error'
          }`}
          validate={[required, facilityCodeValidation]}
          name="facilityCode"
          normalize={normalizeFacilityCode}
          maxLength="4"
          onChange={(e) => {
            const newFacilityCode = normalizeFacilityCode(e.target.value);

            if (newFacilityCode !== facilityCode) {
              searchVenueCode(newFacilityCode, venueCode);
            }
          }}
        />
        <span className="col-auto text-center">&mdash;</span>
        <Field
          component={VenueCodeField}
          className={`form-control form-control-lg form-control--mw-100 text-center ${
            formError && 'venue-code-form-error'
          }`}
          validate={[required, venueCodeValidation]}
          name="venueCode"
          type="tel"
          normalize={normalizeVenueCode}
          maxLength="5"
          onChange={(e) => {
            const newVenueCode = normalizeVenueCode(e.target.value);

            if (newVenueCode !== venueCode) {
              searchVenueCode(facilityCode, normalizeVenueCode(e.target.value));
            }
          }}
        />
      </div>
      {!isBlocked && (
        <SpecialTaxFields
          hasRegionalTax={hasRegionalTax}
          taxRegions={taxRegions}
          isKentuckyEntity={isKentuckyEntity}
          setFormKentuckyCity={setFormKentuckyCity}
        />
      )}
      {genericServerError && (
        <Alert variant="danger" className="mt-3">
          <i className="far fa-exclamation-circle alert__icon" />
          <div className="alert__text">
            Something went wrong. Please try again.
          </div>
        </Alert>
      )}
      {isBlocked && (
        <Alert variant="info" className="mt-3">
          <i className="far fa-exclamation-circle alert__icon" />
          <div className="alert__text">
            Unfortunately, we cannot provide a policy from our online special
            event insurance program for the location provided. For more
            information, please call{' '}
            <span className="text-nowrap">844-747-6240</span>.
          </div>
        </Alert>
      )}
      {address && !isBlocked && (
        <>
          <div className="font-weight-bold">
            Please verify the following address before confirming the venue
          </div>
          <div>
            <i className="fal fa-check-circle text-primary" />
            &nbsp;
            <span className="font-italic">
              {venueName && `${venueName}, `}
              {address}
            </span>
          </div>
        </>
      )}
    </>
  );
};

SearchByVenueCode.propTypes = {
  venueCodeChecked: PropTypes.bool.isRequired,
  validVenueCode: PropTypes.bool.isRequired,
  searchVenueCode: PropTypes.func.isRequired,
  facilityCode: PropTypes.string.isRequired,
  venueCode: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  isBlocked: PropTypes.bool.isRequired,
  genericServerError: PropTypes.bool.isRequired,
  venueName: PropTypes.string.isRequired,
  hasRegionalTax: PropTypes.bool.isRequired,
  taxRegions: PropTypes.arrayOf(venuePropType).isRequired,
  isKentuckyEntity: PropTypes.bool.isRequired,
  setFormKentuckyCity: PropTypes.func.isRequired,
};

export default SearchByVenueCode;
